import GeoValidationDrawer from "components/GeoValidationDrawer";
import styles from "./AddressForm.module.scss";
import { Box, Button, Center, CheckIcon, CloseButton, Group } from "@mantine/core";
import { phoneMask } from "helpers/masks";
import { FC, useEffect, useState } from "react";
import ReactSelect from "react-select";
import { store } from "stores/Store";
import useExternalApiService from "helpers/ExternalApiService";
import { useRegions } from "helpers/useRegionHook";
import QuickFill from "Modules/PractitionerBaseballCard/modules/Details/QuickFill";
import ConfirmationModal from "components/ConfirmationModal/ConfirmationModal";

type GeoValidationAction = "validate" | "draft" | "";

const COUNTRIES = [
  { label: "Australia", value: "AU" },
  { label: "Canada", value: "CA" },
  { label: "United States", value: "US" },
  { label: "Puerto Rico", value: "PR" },
];

export interface AddressFormData {
  address1: string;
  address2: string;
  city: string;
  state: any; // Changed from string to any to accommodate react-select object
  zipcode: string;
  latLon?: [number, number];
  latitude?: number;
  longitude?: number;
  phone: string;
  is_residential: boolean;
  address?: string; //legacy
  country: any;
  is_visiting_address?: boolean; 
}

interface Props {
  practitionerId: string;
  onSave: () => void;
  onClose: () => void;
  addressId?: string;
  initialForm?: Partial<AddressFormData>;
  displayQuickFill?: boolean;
}

const AddressForm: FC<Props> = ({ practitionerId, addressId, initialForm, onSave, onClose, displayQuickFill }) => {
  const [form, setForm] = useState<AddressFormData>({
    address1: initialForm?.address1 || initialForm?.address || "",
    address2: initialForm?.address2 || "",
    city: initialForm?.city || "",
    state: initialForm?.state ?
      { label: initialForm?.state, value: initialForm?.state } as any :
      { label:  "", value: "" } as any,
    zipcode: initialForm?.zipcode || "",
    latLon: initialForm?.latitude && initialForm?.longitude ? 
      [initialForm.latitude, initialForm.longitude] : undefined,
    phone: initialForm?.phone || "",
    is_residential: initialForm?.is_residential || false,
    country: initialForm?.country || "US",
  });
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { patchData, postData, deleteData } = useExternalApiService(apiUrl);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [formState, setFormState] = useState<Partial<AddressFormData>>({});
  const [geoValidationAction, setGeoValidationAction] = useState<GeoValidationAction>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isGeoValidated = !!form?.latLon;
  const isFormValid = Object.keys(formState).length === 0;
  const regions = useRegions(form.country);
  const [isFormOnView, setIsFormOnView] = useState(false);

  const handleGeoValidation = (action: GeoValidationAction = "validate") => {
    setGeoValidationAction(action);
  };

  const handleDelete = async () => {
    if (!addressId) return;
    
    setIsSubmitting(true);
    try {
      await deleteData(`/api/practitioner/${practitionerId}/address-delete/${addressId}`);
      
      const response = await postData('/api/get_practitioner_by_id', {
        practitioner_id: practitionerId
      });
      store.clearPractitionersCache();
      store.setCurrentPractitioner(response.practitioner);
      setIsDeleteModalOpen(false);
      setIsSubmitting(false);
      onSave();
    } catch (error) {
      console.error('Error deleting address:', error);
      setIsSubmitting(false);
    }
  };

  const createAddress = async (f: AddressFormData) => {
    setIsSubmitting(true);
    if (addressId) {
      await patchData(`/api/practitioner/${practitionerId}/address/${addressId}`, {
        address_data: {
          address: `${f.address1}\n${f.address2}\n${f?.city}, ${(f?.state as any)?.value} ${f?.zipcode}`,
          address1: `${f.address1}`,
          address2: `${f.address2}`,
          phone: f.phone,
          latitude: (f?.latLon ? f.latLon[0] : undefined),
          longitude: (f?.latLon ? f.latLon[1] : undefined),
          city: f.city,
          state: (f.state as any)?.value,
          zipcode: f.zipcode,
          country: f.country,
          is_residential: f.is_residential ? 1 : 0,
        }
      });
    } else {
      await postData(`/api/practitioner/${practitionerId}/address`, {
        address_data: {
          address: `${f.address1}\n${f.address2}\n${f?.city}, ${(f?.state as any)?.value} ${f?.zipcode}`,
          address1: `${f.address1}`,
          address2: `${f.address2}`,
          phone: f.phone,
          latitude: (f?.latLon ? f.latLon[0] : undefined),
          longitude: (f?.latLon ? f.latLon[1] : undefined),
          city: f.city,
          state: (f.state as any)?.value,
          zipcode: f.zipcode,
          country: f.country,
          is_residential: f.is_residential ? 1 : 0,
        }
      });
    }

    const response = await postData('/api/get_practitioner_by_id', {
      practitioner_id: practitionerId
    })
    store.clearPractitionersCache();
    store.setCurrentPractitioner(response.practitioner);
    setIsSubmitting(false);
    onSave();
  };

  useEffect(() => {
    const errObj: { [k: string]: string } = {};

    if (!form.address1) {
      errObj["address1"] = "empty";
    }

    if (!form.latLon) {
      errObj["latLon"] = "empty";
    }

    if (!form.state) {
      errObj["state"] = "empty";
    }

    if (!form.zipcode) {
      errObj["zipcode"] = "empty";
    }

    if (!form.city) {
      errObj["city"] = "empty";
    }

    if (!form.country) {
      errObj["country"] = "empty";
    }

    setFormState(errObj);
  }, [form]);

  if (!addressId && (displayQuickFill && !isFormOnView)) {
    return (
      <Box>
        <Group justify="flex-end">
          <CloseButton onClick={onClose} />
        </Group>
        <QuickFill
          onViewChange={(addressInfo) => {
            if (addressInfo) {
              const address1FromPlaces = (addressInfo?.place?.address_components?.find((a: any) => a?.types?.includes("street_number"))?.long_name || "") + " " + (addressInfo?.place?.address_components?.find((a: any) => a?.types?.includes("route"))?.long_name || "");
              const address2FromPlaces = addressInfo?.place?.address_components?.find((a: any) => a?.types?.includes("subpremise"))?.long_name || "";

              const stateFromPlaces = addressInfo?.place?.address_components?.find((a: any) => a?.types?.includes("administrative_area_level_1"))?.short_name;

              setForm({
                address1: address1FromPlaces,
                address2: address2FromPlaces,
                city: addressInfo?.place?.address_components?.find((a: any) => a?.types?.includes("locality"))?.long_name || 
                  addressInfo?.place?.address_components?.find((a: any) => a?.types?.includes("administrative_area_level_2"))?.long_name,
                state: stateFromPlaces ?
                  { label: stateFromPlaces, value: stateFromPlaces } as any :
                  { label: "", value: "" } as any,
                zipcode: addressInfo?.place?.address_components?.find((a: any) => a?.types?.includes("postal_code"))?.long_name,
                latLon: addressInfo?.place?.geometry?.location?.lat ? [
                  addressInfo?.place?.geometry?.location?.lat,
                  addressInfo?.place?.geometry?.location?.lng
                ] : undefined,
                phone: addressInfo?.place?.formatted_phone_number,
                is_residential: false,
                country: "US",
              });
            }
            setIsFormOnView(true);
          }}
        />
       </Box>
    );
  }

  return (
    <Box>
      <Group justify="flex-end">
        <CloseButton onClick={onClose} />
      </Group>
      <GeoValidationDrawer
        opened={!!geoValidationAction}
        onClose={() => setGeoValidationAction("")}
        addressToValidate={{
          administrativeArea: (form.state as any)?.value || "",
          locality: form.city || "",
          postalCode: form.zipcode || "",
          addressLines: [form.address1 || "", form.address2 || ""],
          regionCode: form.country || "",
        }}
        onConfirm={(a) => {
          if (a) {
            const f = {
              ...form,
              state: { label: a.administrativeArea, value: a.administrativeArea },
              city: a.locality,
              address1: a.addressLines[0],
              address2: a.addressLines[1],
              zipcode: a.postalCode,
              latLon: a.latLon,
              country: form.country,
            };
            setForm(f);
            if (geoValidationAction === "draft") {
              alert("create");
            }
          } else {
            const f = {
              ...form,
              latLon: undefined
            };
            setForm(f);
            if (geoValidationAction === "draft") {
              alert("create")
            }
          }
          setGeoValidationAction("");
        }}
      />
      <div className={styles["form__input"]}>
        <div className={styles["form__input-header"]}>
          <label className={styles["form__input-label"]}>
            Address<span className={styles.required}>*</span>
          </label>
          <label className={styles["form__checkbox-label"]}>
            <Button
              onClick={() => handleGeoValidation()}
              leftSection={isGeoValidated ? <CheckIcon size={10} /> : null}
              variant="transparent">
              Geo-Validate
            </Button>
          </label>
        </div>
        <input
          className={formState.address1 ? styles["form__input--error"] : ""}
          value={form.address1}
          placeholder="123 Wellness Road"
          onChange={(e) => setForm({ ...form, address1: e.target.value })}
          type="text" />
        <input
          className={formState.address2 ? styles["form__input--error"] : ""}
          value={form.address2}
          onChange={(e) => setForm({ ...form, address2: e.target.value })}
          placeholder="Suite 456"
          type="text" />
      </div>
      <div className={styles["form__input"]}>
        <label>
          Country<span className={styles.required}>*</span>
        </label>
        <ReactSelect
          value={COUNTRIES.find(country => country.value === form.country)}
          className={styles["form__input--select"]}
          onChange={(v: any) => setForm({ ...form, country: v.value })}
          placeholder="United States"
          styles={{
            control: (baseStyles: any) => ({
              ...baseStyles,
              border: 'none',
              borderBottom: '1px solid #ccc',
              paddingLeft: 0,
              marginLeft: 0,
              color: '#4F7D9C',
              boxShadow: 'none',
              minHeight: "30px",
              maxHeight: "30px",
              '&:hover': {
                boxShadow: 'none'
              },

            }),
            dropdownIndicator: (baseStyles: any) => ({
              ...baseStyles,
              marginTop: "-5px",
            }),
            valueContainer: (baseStyles: any) => ({
              ...baseStyles,
              paddingLeft: 0,
              marginLeft: 0,
            }),
            indicatorSeparator: () => ({
              display: 'none'
            })
          }}
          options={COUNTRIES.map((country) => ({
            label: country.label,
            value: country.value,
          })) as []} />
      </div>
      <div className={styles["form__input"]}>
        <label>
          {form.country === "AU" ? "Suburb" : "City"}<span className={styles.required}>*</span>
        </label>
        <input
          className={formState.city ? styles["form__input--error"] : ""}
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
          placeholder="Palm Coast"
          type="text" />
      </div>
      <div className={styles["form__side-by-side"]}>
        <div className={styles["form__input"]}>
          <label>
            {form.country === "CA" ? "Province/State" : form.country === "AU" ? "State/Territory" : "State"}<span className={styles.required}>*</span>
          </label>
          <ReactSelect
            value={form.state}
            className={styles["form__input--select"]}
            onChange={(v: any) => setForm({ ...form, state: v })}
            placeholder="FL"
            menuPlacement="top"
            styles={{
              control: (baseStyles: any) => ({
                ...baseStyles,
                border: 'none',
                borderBottom: '1px solid #ccc',
                paddingLeft: 0,
                marginLeft: 0,
                color: '#4F7D9C',
                boxShadow: 'none',
                minHeight: "30px",
                maxHeight: "30px",
                '&:hover': {
                  boxShadow: 'none'
                },

              }),
              dropdownIndicator: (baseStyles: any) => ({
                ...baseStyles,
                marginTop: "-5px",
              }),
              valueContainer: (baseStyles: any) => ({
                ...baseStyles,
                paddingLeft: 0,
                marginLeft: 0,
              }),
              indicatorSeparator: () => ({
                display: 'none'
              })
            }}
            options={regions.map((s) => ({
              label: s.label,
              value: s.value,
            })) as []} />
        </div>
        <div className={styles["form__input"]}>
          <label>
            {form.country === "US" ? "Zipcode" : "Postal Code"}<span className={styles.required}>*</span>
          </label>
          <input
            className={formState.zipcode ? styles["form__input--error"] : ""}
            value={form.zipcode}
            onChange={(e) => setForm({ ...form, zipcode: e.target.value })}
            placeholder="01234"
            type="text"
          />
        </div>
      </div>
      <div className={styles["form__side-by-side"]}>
        <div className={styles["form__input"]}>
          <label>
            Phone
          </label>
          <input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: phoneMask(e.target.value, form.country) })}
            placeholder="(888)888-8888"
            type="text" />
        </div>
        <div className={styles["form__input"]}>
          <label>
            Is Residential?
          </label>
          <input
            checked={form.is_residential}
            type="checkbox"
            onChange={(e) => setForm({ ...form, is_residential: !form.is_residential })} 
            />
        </div>
      </div>
      <Center mt="xl">
        <Group style={{ width: '100%', gap: '10px' }}>
          {addressId && initialForm?.is_visiting_address && (
            <Button 
              bg="#E64B37"
              size="lg"
              style={{ 
                height: '60px', 
                fontSize: '20px', 
                fontWeight: 'bold',
                flex: 1
              }}
              loading={isSubmitting}
              onClick={() => setIsDeleteModalOpen(true)}>
              Delete
            </Button>
          )}
          <Button 
            size="lg"
            style={{ 
              height: '60px', 
              fontSize: '20px', 
              fontWeight: 'bold',
              flex: 1,
              backgroundColor: '#00BE3F',
            }}
            loading={isSubmitting}
            disabled={!isFormValid} 
            onClick={() => createAddress(form)}>
            Save
          </Button>
        </Group>
      </Center>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Visiting Address"
        message="Are you sure you want to delete this visiting address?"
        confirmText="DELETE"
        cancelText="CANCEL"
        modalWrapperStyle={{
          position: 'absolute',
          bottom: window.innerWidth >= 1024 ? '100px' : '20px',
          left: 0,
          right: 0,
        }}
      />
    </Box>
  );
};

export default AddressForm;